import { useState, useEffect, useCallback } from "react";
import type { ConversationThreadView } from "../../../../store/types";
import { supabase } from "../../../../lib/supabaseClient";

const SYSTEM_NOTICE_TEMPLATES = [
  "【平台公告】提醒您，攝影服務所有費用請透過平台官方渠道支付，請勿直接付款給攝影師。",
  "【行程確認】您的攝影服務集合時間與地點已確認，請查看訊息中的詳細資訊。",
  "【重要提醒】請勿在平台外與攝影師進行任何金錢往來或私下訂單協商。",
  "【服務說明】TaBiFoTo 提供攝影服務，不包含任何交通、導覽或行程規劃。",
];

interface RelayEvent {
  id: string;
  event_type: string;
  actor_type: string;
  actor_id: string;
  payload: Record<string, unknown>;
  created_at: string;
}

const EVENT_ICON: Record<string, string> = {
  thread_created: "ri-add-circle-line",
  message_blocked: "ri-forbid-line",
  risk_flagged: "ri-flag-line",
  status_changed: "ri-git-commit-line",
  dispatch_notice_sent: "ri-send-plane-line",
  relay_notice_created: "ri-notification-badge-line",
};

const EVENT_COLOR: Record<string, string> = {
  thread_created: "text-emerald-500",
  message_blocked: "text-red-400",
  risk_flagged: "text-orange-400",
  status_changed: "text-[#C8B8A6]",
  dispatch_notice_sent: "text-sky-400",
  relay_notice_created: "text-teal-500",
};

const EVENT_LABEL: Record<string, string> = {
  thread_created: "訊息串已建立",
  message_blocked: "訊息已封鎖",
  risk_flagged: "風險標記",
  status_changed: "狀態變更",
  dispatch_notice_sent: "排班通知已送出",
  relay_notice_created: "Relay 通知建立",
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-TW", {
    month: "numeric", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface Props {
  thread: ConversationThreadView | null;
  onSendSystemNotice: (body: string) => Promise<void>;
}

export default function RelayOrderSummary({ thread, onSendSystemNotice }: Props) {
  const [noticeInput, setNoticeInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [relayEvents, setRelayEvents] = useState<RelayEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const fetchRelayEvents = useCallback(async (threadId: string) => {
    if (!supabase) return;
    setLoadingEvents(true);
    const { data } = await supabase
      .from("relay_events")
      .select("id, event_type, actor_type, actor_id, payload, created_at")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: false })
      .limit(12);
    if (data) setRelayEvents(data as RelayEvent[]);
    setLoadingEvents(false);
  }, []);

  useEffect(() => {
    if (thread?.id) {
      void fetchRelayEvents(thread.id);
    } else {
      setRelayEvents([]);
    }
  }, [thread?.id, fetchRelayEvents]);

  const handleSend = async () => {
    const trimmed = noticeInput.trim();
    if (!trimmed || sending) return;
    setSending(true);
    await onSendSystemNotice(trimmed);
    setNoticeInput("");
    setSending(false);
    setSent(true);
    setTimeout(() => setSent(false), 2500);
    if (thread?.id) void fetchRelayEvents(thread.id);
  };

  if (!thread) {
    return (
      <div className="w-64 xl:w-72 border-l border-[#E8E0D5] bg-white flex items-center justify-center">
        <p className="text-xs text-[#2C2C2C]/25 font-light px-4 text-center">選擇訊息串以查看詳情</p>
      </div>
    );
  }

  const isRisky = thread.risk_level !== "none";
  const participantLabel = thread.participant_type === "customer" ? "客戶溝通" : thread.participant_type === "photographer" ? "攝影師溝通" : "內部";

  const dispatchEvents = relayEvents.filter(
    (e) => e.event_type === "dispatch_notice_sent" || e.event_type === "relay_notice_created"
  );

  return (
    <div className="w-64 xl:w-72 border-l border-[#E8E0D5] bg-white overflow-y-auto shrink-0 h-full">
      <div className="px-4 py-4 space-y-5">

        {/* Dispatch notice indicator */}
        {dispatchEvents.length > 0 && (
          <section>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center text-teal-500 shrink-0">
                <i className="ri-notification-badge-line text-sm" />
              </div>
              <div>
                <p className="text-[10px] font-medium text-teal-700">排班通知已建立</p>
                <p className="text-[9px] text-teal-600/70 font-light">由排班系統自動觸發</p>
              </div>
            </div>
          </section>
        )}

        <section>
          <h3 className="text-[10px] uppercase tracking-widest text-[#2C2C2C]/35 mb-2.5 font-light">訂單摘要</h3>
          <div className="bg-[#F7F6F3] rounded-lg p-3 space-y-2">
            {thread.order_number && <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/40">訂單編號</span><span className="text-[10px] font-medium text-[#2C2C2C]">{thread.order_number}</span></div>}
            {thread.customer_name && <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/40">客戶</span><span className="text-[10px] text-[#2C2C2C]">{thread.customer_name}</span></div>}
            {thread.selected_city && <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/40">城市</span><span className="text-[10px] text-[#2C2C2C]">{thread.selected_city}</span></div>}
            {thread.shoot_date && <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/40">拍攝日期</span><span className="text-[10px] text-[#2C2C2C]">{thread.shoot_date}</span></div>}
            <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/40">類型</span><span className="text-[10px] text-[#2C2C2C]">{participantLabel}</span></div>
          </div>
        </section>

        {isRisky && (
          <section>
            <h3 className={`text-[10px] uppercase tracking-widest mb-2.5 font-light ${thread.risk_level === "high" ? "text-red-400" : thread.risk_level === "medium" ? "text-orange-400" : "text-yellow-500"}`}>⚑ 風險 — {thread.risk_level}</h3>
            <div className={`rounded-lg p-3 border ${thread.risk_level === "high" ? "bg-red-50 border-red-100" : thread.risk_level === "medium" ? "bg-orange-50 border-orange-100" : "bg-yellow-50 border-yellow-100"}`}>
              <p className="text-xs font-light leading-relaxed text-[#2C2C2C]/70">{thread.risk_summary || "此訊息串已被標記為風險，請審查訊息內容。"}</p>
            </div>
          </section>
        )}

        {!isRisky && (
          <section>
            <h3 className="text-[10px] uppercase tracking-widest text-[#2C2C2C]/35 mb-2.5 font-light">風險監控</h3>
            <div className="bg-[#F7F6F3] rounded-lg p-3 space-y-1.5">
              {["監控平台外付款請求", "注意私人聯絡資訊分享", "防止服務混淆", "追蹤私下議價行為"].map((item) => (
                <div key={item} className="flex items-start gap-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center text-[#C8B8A6] mt-0.5 shrink-0"><i className="ri-shield-check-line text-xs" /></div>
                  <span className="text-[10px] text-[#2C2C2C]/50 font-light">{item}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Relay Events Timeline */}
        <section>
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-[10px] uppercase tracking-widest text-[#2C2C2C]/35 font-light">稽核軌跡</h3>
            <button
              onClick={() => thread?.id && void fetchRelayEvents(thread.id)}
              className="w-4 h-4 flex items-center justify-center text-[#2C2C2C]/20 hover:text-[#2C2C2C]/50 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line text-[10px]" />
            </button>
          </div>
          {loadingEvents ? (
            <p className="text-[10px] text-[#2C2C2C]/25 font-light py-1">載入中...</p>
          ) : relayEvents.length === 0 ? (
            <p className="text-[10px] text-[#2C2C2C]/25 font-light italic">尚無軌跡記錄</p>
          ) : (
            <div className="relative space-y-2.5 pl-4 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-[#E8E0D5]">
              {relayEvents.map((ev) => {
                const icon = EVENT_ICON[ev.event_type] ?? "ri-record-circle-line";
                const color = EVENT_COLOR[ev.event_type] ?? "text-[#C8B8A6]";
                const label = EVENT_LABEL[ev.event_type] ?? ev.event_type;
                const isDispatch = ev.event_type === "dispatch_notice_sent" || ev.event_type === "relay_notice_created";
                return (
                  <div key={ev.id} className="relative">
                    <div className={`absolute -left-4 top-0.5 w-3 h-3 flex items-center justify-center ${color}`}>
                      <i className={`${icon} text-[10px]`} />
                    </div>
                    <div className={`rounded-md px-2 py-1.5 ${isDispatch ? "bg-teal-50 border border-teal-100" : "bg-[#F7F6F3]"}`}>
                      <p className={`text-[10px] font-medium ${isDispatch ? "text-teal-700" : "text-[#2C2C2C]/70"}`}>
                        {label}
                        {isDispatch && <span className="ml-1 text-[9px] font-light text-teal-500">· 排班系統</span>}
                      </p>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-[9px] text-[#2C2C2C]/30 font-light">{ev.actor_type} / {ev.actor_id.slice(0, 8)}</span>
                        <span className="text-[9px] text-[#2C2C2C]/25">{formatTime(ev.created_at)}</span>
                      </div>
                      {ev.payload && Object.keys(ev.payload).length > 0 && (
                        <p className="text-[9px] text-[#2C2C2C]/35 font-light mt-0.5 truncate">
                          {Object.entries(ev.payload).slice(0, 2).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h3 className="text-[10px] uppercase tracking-widest text-[#2C2C2C]/35 mb-2.5 font-light">系統通知</h3>
          <div className="space-y-2">
            <div className="space-y-1">
              {SYSTEM_NOTICE_TEMPLATES.map((tpl) => (
                <button key={tpl} onClick={() => setNoticeInput(tpl)}
                  className="w-full text-left px-2.5 py-1.5 rounded-md bg-[#F7F6F3] border border-[#E8E0D5] text-[9px] text-[#2C2C2C]/50 hover:text-[#2C2C2C]/80 hover:border-[#C8B8A6] transition-colors cursor-pointer leading-relaxed">
                  {tpl.length > 45 ? tpl.slice(0, 45) + "..." : tpl}
                </button>
              ))}
            </div>
            <textarea value={noticeInput} onChange={(e) => setNoticeInput(e.target.value)} placeholder="自訂系統通知..." rows={3}
              className="w-full px-2.5 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#C8B8A6] resize-none text-sm" />
            <button onClick={handleSend} disabled={!noticeInput.trim() || sending}
              className="w-full py-2 rounded-lg bg-[#2C2C2C] text-white text-xs font-light disabled:opacity-40 hover:bg-[#444] transition-colors cursor-pointer whitespace-nowrap">
              {sent ? "✓ 已送出" : sending ? "傳送中..." : "傳送系統通知"}
            </button>
          </div>
        </section>

        <section>
          <h3 className="text-[10px] uppercase tracking-widest text-[#2C2C2C]/35 mb-2 font-light">訊息串資訊</h3>
          <div className="space-y-1">
            <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/35">建立時間</span><span className="text-[10px] text-[#2C2C2C]/50">{new Date(thread.created_at).toLocaleDateString("zh-TW")}</span></div>
            <div className="flex justify-between"><span className="text-[10px] text-[#2C2C2C]/35">最後更新</span><span className="text-[10px] text-[#2C2C2C]/50">{new Date(thread.updated_at).toLocaleDateString("zh-TW")}</span></div>
          </div>
        </section>
      </div>
    </div>
  );
}
